export type SpendSummaryTotals = {
    totalSpend: number;
    totalContribution: number;
    totalContributionProduct: number;
};

/**
 * Which variables the totals were taken over, so the KPI can say what it covers.
 * Raw variable names — map them through `variableNameMap` before display.
 *
 * Variables with **no spend** in the period appear in neither list, and that is
 * deliberate. Spend Summary is a spend-focused screen: variables that contribute
 * without spend belong to the contribution-focused dashboard, and the query already
 * drops anything with `SUM("Spend") <= 0` across the dataset
 * (`spend_summary_filtered_empty_vars`). Reporting the in-period remainder as
 * "excluded" would raise a distinction the rest of this screen does not make.
 */
export type ModeledVariablePartition = {
    /** Both spend and contribution present — the ROI population. */
    includedVariables: string[];
    /** Spend but no contribution. The case this setting exists to remove. */
    spendWithoutContribution: string[];
};

export type ModeledVariablesResult = SpendSummaryTotals &
    ModeledVariablePartition;

type ModeledVariableRow = {
    variable: string;
    spend: number;
    contribution: number;
    contributionProduct: number;
};

/**
 * Totals over only those variables that have BOTH spend and contribution in `rows`.
 *
 * Variables with spend but no contribution inflate the ROI denominator without
 * contributing to the numerator, which understates ROI. Used for the Total Media ROI
 * KPI only, when the dashboard's `modeledVariablesOnlyInTotalMediaRoi` setting is on.
 *
 * Grouped by raw `variable` rather than display label, so two variables sharing a
 * label cannot be merged. Presence is tested on the sum of absolute values, so a
 * variable whose positive and negative contributions happen to cancel over the
 * period still counts as modeled; the amounts returned are the signed sums.
 *
 * `rows` is already scoped to the selected date range, so the predicate is too —
 * a variable with spend but no contribution *in this period* should not sit in
 * this period's denominator.
 *
 * Every field is coerced because `Spend` and `Contribution` are nullable in
 * `output_t_dataset_variables` (built by `st_read` over the xlsx, and
 * `mergeUnpivotedTable` applies no `COALESCE`) despite what the zod schema declares.
 */
export const sumTotalsForModeledVariables = (
    rows: ModeledVariableRow[],
): ModeledVariablesResult => {
    const byVariable = new Map<
        string,
        SpendSummaryTotals & { absSpend: number; absContribution: number }
    >();

    for (const row of rows) {
        const spend = Number(row.spend) || 0;
        const contribution = Number(row.contribution) || 0;
        const contributionProduct = Number(row.contributionProduct) || 0;

        const totals = byVariable.get(row.variable) ?? {
            totalSpend: 0,
            totalContribution: 0,
            totalContributionProduct: 0,
            absSpend: 0,
            absContribution: 0,
        };

        totals.totalSpend += spend;
        totals.totalContribution += contribution;
        totals.totalContributionProduct += contributionProduct;
        totals.absSpend += Math.abs(spend);
        totals.absContribution += Math.abs(contribution);

        byVariable.set(row.variable, totals);
    }

    const result: ModeledVariablesResult = {
        totalSpend: 0,
        totalContribution: 0,
        totalContributionProduct: 0,
        includedVariables: [],
        spendWithoutContribution: [],
    };

    for (const [variable, totals] of byVariable) {
        if (totals.absSpend === 0) {
            continue;
        }

        if (totals.absContribution === 0) {
            result.spendWithoutContribution.push(variable);
            continue;
        }

        result.includedVariables.push(variable);
        result.totalSpend += totals.totalSpend;
        result.totalContribution += totals.totalContribution;
        result.totalContributionProduct += totals.totalContributionProduct;
    }

    return result;
};
